/**
 * Shared axes used by the deterministic case-study generator.
 * Pure data, no AI calls: identical output on every build.
 */
import { PROVINCES } from "@/data/coverage";

export const REGION_TH: Record<string, string> = {
  bangkok: "กรุงเทพฯ และปริมณฑล",
  central: "ภาคกลาง",
  north: "ภาคเหนือ",
  northeast: "ภาคอีสาน",
  east: "ภาคตะวันออก",
  west: "ภาคตะวันตก",
  south: "ภาคใต้",
};

export const REGION_SLUGS = Object.keys(REGION_TH);

export function provincesByRegion(region: string): string[] {
  const list = PROVINCES.filter((p) => p.region === region).map((p) => p.nameTh);
  return list.length ? list : PROVINCES.map((p) => p.nameTh);
}

export type Audience = { slug: string; th: string; nationalities: string[] };

export const AUDIENCES: Audience[] = [
  {
    slug: "student",
    th: "นักเรียน นักศึกษา และผู้สมัครทุน",
    nationalities: ["ชาวไทย", "ชาวไทย", "ชาวจีน", "ชาวเวียดนาม", "ชาวอินเดีย"],
  },
  {
    slug: "worker",
    th: "ผู้ทำงานและผู้ย้ายถิ่นเพื่อทำงาน",
    nationalities: ["ชาวไทย", "ชาวฟิลิปปินส์", "ชาวเมียนมา", "ชาวญี่ปุ่น", "ชาวอินเดีย"],
  },
  {
    slug: "spouse",
    th: "คู่สมรสและครอบครัวข้ามชาติ",
    nationalities: ["ชาวไทย", "ชาวเยอรมัน", "ชาวอังกฤษ", "ชาวออสเตรเลีย", "ชาวอเมริกัน"],
  },
  {
    slug: "business",
    th: "ผู้ประกอบการและนิติบุคคล",
    nationalities: ["ชาวไทย", "ชาวสิงคโปร์", "ชาวจีน", "ชาวเกาหลีใต้", "ชาวดัตช์"],
  },
  {
    slug: "retiree",
    th: "ผู้เกษียณและผู้พำนักระยะยาว",
    nationalities: ["ชาวอังกฤษ", "ชาวสวิส", "ชาวฝรั่งเศส", "ชาวอเมริกัน", "ชาวไทย"],
  },
];

export type Destination = { slug: string; th: string; countries: string[] };

export const DESTINATIONS: Destination[] = [
  { slug: "oceania", th: "ออสเตรเลียและนิวซีแลนด์", countries: ["ออสเตรเลีย", "นิวซีแลนด์"] },
  {
    slug: "europe",
    th: "ยุโรปและกลุ่มเชงเกน",
    countries: ["เยอรมนี", "ฝรั่งเศส", "เนเธอร์แลนด์", "สวีเดน", "อิตาลี", "สเปน"],
  },
  { slug: "north-america", th: "อเมริกาเหนือ", countries: ["สหรัฐอเมริกา", "แคนาดา"] },
  { slug: "east-asia", th: "เอเชียตะวันออก", countries: ["ญี่ปุ่น", "เกาหลีใต้", "จีน", "ไต้หวัน"] },
  { slug: "middle-east", th: "ตะวันออกกลาง", countries: ["สหรัฐอาหรับเอมิเรตส์", "กาตาร์", "ซาอุดีอาระเบีย"] },
  { slug: "asean", th: "อาเซียนและเอเชียใต้", countries: ["สิงคโปร์", "เวียดนาม", "มาเลเซีย", "อินเดีย", "อินโดนีเซีย"] },
];

export const TURNAROUNDS: string[] = [
  "ประมาณ 1–2 วันทำการหลังได้รับเอกสารครบ (โดยประมาณ)",
  "ประมาณ 2–3 วันทำการ (โดยประมาณ ขึ้นกับคิวหน่วยงาน)",
  "ประมาณ 3–5 วันทำการ (โดยประมาณ)",
  "ประมาณ 5–7 วันทำการ (โดยประมาณ รวมเวลาส่งเอกสาร)",
  "ประมาณ 7–10 วันทำการ (โดยประมาณ เมื่อมีขั้นตอนรับรองต่อเนื่องหลายหน่วยงาน)",
  "ประมาณ 2–4 สัปดาห์ (โดยประมาณ เมื่อรอคิวหน่วยงานปลายทาง)",
];

export type Hurdle = { obstacle: string; resolution: string };

export const HURDLES: Hurdle[] = [
  {
    obstacle: "ชื่อ-นามสกุลบนหนังสือเดินทางสะกดไม่ตรงกับเอกสารทะเบียนราษฎรฉบับเก่า",
    resolution: "ทำตารางเทียบการสะกดแนบท้ายคำแปล และแนบเอกสารเปลี่ยนชื่อเป็นหลักฐานความต่อเนื่องของบุคคลเดียวกัน",
  },
  {
    obstacle: "สำเนาเอกสารที่ผู้ใช้บริการส่งมาเป็นภาพถ่ายจากมือถือ ความคมชัดไม่พอสำหรับงานรับรอง",
    resolution: "แนะนำวิธีสแกนความละเอียดที่ใช้ได้ และตรวจอ่านทุกหน้าอีกครั้งก่อนเข้าขั้นตอนรับรอง",
  },
  {
    obstacle: "หน่วยงานปลายทางกำหนดอายุเอกสารไม่เกิน 3 เดือน แต่ฉบับที่มีอยู่ออกมานานแล้ว",
    resolution: "จัดลำดับให้ขอคัดฉบับใหม่ก่อน แล้วจึงเดินงานแปลและรับรองต่อเนื่องเพื่อไม่ให้อายุเอกสารหมดกลางทาง",
  },
  {
    obstacle: "ผู้ใช้บริการอยู่ต่างจังหวัดและไม่สะดวกเดินทางเข้ามายื่นเอกสารด้วยตนเอง",
    resolution: "ใช้ช่องทางรับ-ส่งเอกสารทางไปรษณีย์และขนส่งเอกชน พร้อมยืนยันความครบถ้วนทางออนไลน์ก่อนส่งจริง",
  },
  {
    obstacle: "เอกสารต้นฉบับเป็นภาษาที่สาม ทำให้ต้องเลือกลำดับการแปลให้ปลายทางยอมรับ",
    resolution: "ตรวจข้อกำหนดของปลายทางก่อนว่ารับคำแปลภาษาอังกฤษหรือภาษาราชการของประเทศนั้น แล้วจึงกำหนดลำดับงาน",
  },
  {
    obstacle: "รูปแบบวันที่และที่อยู่ในเอกสารไทยไม่ตรงกับรูปแบบที่แบบฟอร์มปลายทางกำหนด",
    resolution: "แปลงรูปแบบวันที่เป็นสากลพร้อมคงข้อความเดิมไว้ในวงเล็บ เพื่อให้ตรวจทานย้อนกลับได้",
  },
  {
    obstacle: "ผู้ใช้บริการเข้าใจว่างานเดียวจบทุกขั้น แต่ปลายทางต้องการการรับรองต่อเนื่องหลายหน่วยงาน",
    resolution: "อธิบายลำดับขั้นเป็นแผนภาพและกำหนดวันคาดหมายของแต่ละขั้น เพื่อวางแผนวันเดินทางได้จริง",
  },
  {
    obstacle: "เอกสารมีตราประทับและลายมือชื่อที่อ่านไม่ออก ทำให้คำแปลอาจถูกตั้งคำถาม",
    resolution: "ระบุในคำแปลว่าเป็นข้อความที่อ่านไม่ชัดตามต้นฉบับ พร้อมทำหมายเหตุตำแหน่งตราประทับให้ตรงกัน",
  },
  {
    obstacle: "กำหนดยื่นของผู้ใช้บริการกระชั้น ขณะที่ขั้นตอนรับรองยังต้องใช้เวลาตามคิวปกติ",
    resolution: "จัดลำดับงานเอกสารชิ้นที่จำเป็นต่อการยื่นก่อน และแจ้งช่วงเวลาที่เป็นไปได้ตามจริงโดยไม่รับประกันผล",
  },
  {
    obstacle: "ข้อมูลในเอกสารสองฉบับขัดกันเอง เช่น ปีเกิดต่างกันหนึ่งปี",
    resolution: "ชี้จุดที่ขัดกันให้ผู้ใช้บริการไปแก้ที่หน่วยงานต้นทางก่อน เพื่อไม่ให้ถูกตีกลับในขั้นรับรอง",
  },
  {
    obstacle: "ผู้ใช้บริการไม่ทราบว่าต้องใช้เอกสารกี่ชุด และปลายทางขอฉบับจริงหรือสำเนารับรอง",
    resolution: "ยืนยันจำนวนชุดและรูปแบบที่ปลายทางต้องการก่อนเริ่มงาน แล้วเตรียมชุดสำรองไว้หนึ่งชุด",
  },
  {
    obstacle: "ต้องมีล่ามร่วมในขั้นตอนลงนามเพราะผู้ลงนามไม่เข้าใจภาษาไทย",
    resolution: "นัดล่ามเข้าร่วมในวันลงนาม และบันทึกไว้ในเอกสารว่ามีการแปลความให้ผู้ลงนามเข้าใจก่อนลงลายมือชื่อ",
  },
  {
    obstacle: "ที่อยู่ปัจจุบันของผู้ใช้บริการไม่ตรงกับที่อยู่ในทะเบียนบ้าน ทำให้เอกสารประกอบไม่สอดคล้อง",
    resolution: "เตรียมหลักฐานที่อยู่ปัจจุบันเพิ่มและอธิบายความต่างไว้ในเอกสารนำส่ง",
  },
  {
    obstacle: "ผู้ใช้บริการอยู่ต่างประเทศ ต่างโซนเวลา ทำให้การยืนยันร่างคำแปลล่าช้า",
    resolution: "กำหนดรอบยืนยันร่างวันละหนึ่งครั้งตามเวลาที่ผู้ใช้บริการสะดวก และสรุปประเด็นแก้ไขเป็นข้อ ๆ",
  },
];

export const OUTCOMES: string[] = [
  "ชุดเอกสารครบตามรูปแบบที่ปลายทางประกาศ ก่อนนำไปยื่นในรอบที่วางแผนไว้",
  "ลดโอกาสถูกขอเอกสารเพิ่ม ด้วยการตรวจข้อมูลทุกฉบับให้ตรงกันก่อนยื่น",
  "วางลำดับขั้นให้สอดคล้องกับวันนัดของหน่วยงาน",
  "ลดรอบการแก้ไข เพราะตรวจชื่อ เลขเอกสาร และวันที่ให้ตรงกันก่อนเข้าขั้นรับรอง",
  "ผู้ใช้เข้าใจลำดับขั้นทั้งหมดและวางแผนวันเดินทางได้ล่วงหน้า",
  "เตรียมชุดสำรองไว้ใช้กับหน่วยงานอื่นในภายหลัง หากปลายทางยอมรับ",
  "แก้ประเด็นที่เคยถูกตีกลับให้ตรงตามข้อกำหนดก่อนยื่นซ้ำ",
  "ผู้อยู่ต่างจังหวัดตรวจได้ว่าขั้นใดดำเนินการทางไกลได้ และขั้นใดต้องมาด้วยตนเอง",
  "แยกให้เห็นว่างานใดเตรียมคู่ขนานได้ และงานใดต้องรอผลขั้นก่อนหน้า",
  "มีรายการเอกสารอ้างอิงสำหรับเตรียมการต่ออายุครั้งถัดไป",
];

/** Deterministic index helper: stable across builds, no randomness. */
export function pick<T>(arr: readonly T[], i: number): T {
  return arr[((i % arr.length) + arr.length) % arr.length]!;
}
